import { getCurrentBrand } from './brandUtils';

const API_BASE = 'https://diymech-api.fly.dev/api/v1';

const getHeaders = () => {
    const brand = getCurrentBrand();
    const headers: any = {
        'Content-Type': 'application/json',
        'X-Brand': brand,
        'X-Client-App': 'diyelectronicmech'
    };

    const savedUser = localStorage.getItem('diymech_user');
    if (savedUser) {
        const user = JSON.parse(savedUser);
        headers['X-User-Id'] = user.id;
        headers['X-User-Email'] = user.email;
    }

    return headers;
};

let currentSessionId = `session-${Date.now()}`;

export const resetSession = () => {
    currentSessionId = `session-${Date.now()}`;
};

export interface Message {
  role: 'user' | 'assistant';
  content: string;
  diagrams?: any[];
  evidence_context?: any[];
  confidence_score?: number;
}

export interface MaintenanceSpec {
    make: string;
    model: string;
    year: number;
    engine?: string;
    oil_type?: string;
    oil_capacity?: string;
    transmission_fluid?: string;
    coolant_type?: string;
    brake_fluid?: string;
    power_steering_fluid?: string;
    wiper_sizes?: string[];
    spark_plug_type?: string;
    spark_plug_gap?: string;
    // Electronics additions
    solder_type?: string;
    flux_type?: string;
    cleaning_ipa?: string;
    thermal_paste?: string;
    kapton_tape?: string;
    solder_wick?: string;
    notes?: string;
    source?: string;
    generic?: boolean;
}

export const api = {
    async loginWithGoogle(idToken: string) {
        try {
            const response = await fetch(`${API_BASE}/auth/google`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ id_token: idToken })
            });
            if (!response.ok) throw new Error(`Status: ${response.status}`);
            return await response.json();
        } catch (error: any) {
            console.error('Login API Error:', error);
            throw error;
        }
    },

    async chat(message: string, vehicleContext: any = {}, history: any[] = []) {
        try {
            const response = await fetch(`${API_BASE}/chat/send`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({
                    message,
                    vehicle_info: {
                        make: vehicleContext.make || 'Electronics',
                        model: vehicleContext.model || 'Generic',
                        year: vehicleContext.year || 2026,
                        type: 'Electronics'
                    },
                    conversation_history: history,
                    session_id: currentSessionId
                })
            });
            if (!response.ok) throw new Error(`Status: ${response.status}`);
            return await response.json();
        } catch (error: any) {
            console.error('Chat API Error:', error);
            throw new Error(`Direct connection failed. ${error.message}`);
        }
    },

    async searchParts(query: string, vehicleInfo: any = {}, limit: number = 100) {
        const params = new URLSearchParams();
        params.append('q', query);
        if (vehicleInfo.make) params.append('make', vehicleInfo.make);
        if (vehicleInfo.model) params.append('model', vehicleInfo.model);
        if (vehicleInfo.year) params.append('year', String(vehicleInfo.year));
        params.append('limit', String(limit));

        try {
            const response = await fetch(`${API_BASE}/parts/search?${params.toString()}`, {
                headers: getHeaders()
            });
            if (!response.ok) throw new Error(`Status: ${response.status}`);
            return await response.json();
        } catch (error: any) {
            console.error('Parts API Error:', error);
            throw new Error(`Parts search failed. ${error.message}`);
        }
    },

    async searchDiagrams(query: string) {
        try {
            const response = await fetch(`${API_BASE}/diagrams/search?q=${encodeURIComponent(query)}&deep=true`, {
                headers: getHeaders()
            });
            if (!response.ok) throw new Error(`Status: ${response.status}`);
            return await response.json();
        } catch (error: any) {
            console.error('Diagrams API Error:', error);
            throw new Error(`Diagram search failed. ${error.message}`);
        }
    },

    async generateVisionDiagram(query: string, vehicleInfo: any = {}, referenceURLs: string[] = []) {
        try {
            const response = await fetch(`${API_BASE}/diagrams/generate-vision`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({
                    query,
                    vehicle_descriptor: `${vehicleInfo.year || ''} ${vehicleInfo.make || ''} ${vehicleInfo.model || ''}`.trim(),
                    diagram_type: 'custom',
                    reference_image_urls: referenceURLs
                })
            });
            if (!response.ok) throw new Error(`Status: ${response.status}`);
            return await response.json();
        } catch (error: any) {
            console.error('Vision Diagram Error:', error);
            throw error;
        }
    },

    async getBrandConfig() {
        try {
            const brand = getCurrentBrand();
            const response = await fetch(`${API_BASE}/brands/${brand}/config`, {
                headers: getHeaders()
            });
            if (!response.ok) throw new Error(`Status: ${response.status}`);
            return await response.json();
        } catch (error: any) {
            console.error('Brand Config Error:', error);
            throw error;
        }
    },

    async analyzeImage(file: File, description: string) {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('description', description);

        try {
            const brand = getCurrentBrand();
            const response = await fetch(`${API_BASE}/analyze-image`, {
                method: 'POST',
                headers: { 'X-Brand': brand, 'X-Client-App': (brand === 'diyelectronicmech' ? 'diyelectronicmech' : 'diycarmech') },
                body: formData,
            });
            if (!response.ok) throw new Error(`Status: ${response.status}`);
            return await response.json();
        } catch (error: any) {
            console.error('Image Analysis API Error:', error);
            throw new Error(`Vision AI failed. ${error.message}`);
        }
    },

    async getMaintenanceSpec(filters: { make?: string; model?: string; year?: number; engine?: string }) {
        const params = new URLSearchParams();
        if (filters.make) params.append('make', filters.make);
        if (filters.model) params.append('model', filters.model);
        if (filters.year) params.append('year', String(filters.year));
        if (filters.engine) params.append('engine', filters.engine);

        try {
            const response = await fetch(`${API_BASE}/maintenance/spec?${params.toString()}`, {
                headers: getHeaders()
            });
            if (!response.ok) throw new Error(`Status: ${response.status}`);
            return await response.json();
        } catch (error: any) {
            console.error('Maintenance Spec Error:', error);
            throw error;
        }
    },

    // --- Projects & Offline Storage ---
    async saveProject(project: { id: string; name: string; type: string; data: any; vehicleContext?: any; createdAt?: string }) {
        const headers = getHeaders();
        const userId = headers['X-User-Id'];
        let finalId = project.id;

        if (userId) {
            const backendWs = {
                id: project.id,
                vehicle_make: project.vehicleContext?.make || null,
                vehicle_model: project.vehicleContext?.model || null,
                vehicle_year: project.vehicleContext?.year ? parseInt(project.vehicleContext.year) : null,
                issue_description: project.name,
                type: project.type,
                chat_history: project.type === 'chat' ? (project.data.messages || []) : [],
                parts_list: project.type === 'parts' ? (project.data.results || []) : [],
                diagrams_list: project.type === 'diagrams' ? (project.data.results || []) : [],
            };

            try {
                const updateResp = await fetch(`${API_BASE}/workspaces/${project.id}`, {
                    method: 'PUT',
                    headers,
                    body: JSON.stringify(backendWs)
                });

                if (updateResp.status === 404) {
                    const createResp = await fetch(`${API_BASE}/workspaces`, {
                        method: 'POST',
                        headers,
                        body: JSON.stringify({ ...backendWs, id: undefined })
                    });

                    if (createResp.ok) {
                        const newWs = await createResp.json();
                        if (newWs.id) {
                            finalId = newWs.id;
                        }
                    }
                }
            } catch (err) {
                console.error("Failed to sync project to backend", err);
            }
        }

        const projects = this.getProjectsLocal();
        const index = projects.findIndex((p: any) => p.id === project.id);

        const projectRecord = {
            ...project,
            id: finalId,
            updatedAt: new Date().toISOString()
        };

        if (index !== -1) {
            projects[index] = projectRecord;
        } else {
            if (!projectRecord.createdAt) projectRecord.createdAt = new Date().toISOString();
            projects.push(projectRecord);
        }

        localStorage.setItem('diy_mech_projects', JSON.stringify(projects));
        return finalId;
    },

    getProjectsLocal() {
        const data = localStorage.getItem('diy_mech_projects');
        return data ? JSON.parse(data) : [];
    },

    async getProjects() {
        const headers = getHeaders();
        const userId = headers['X-User-Id'];

        if (userId) {
            try {
                const resp = await fetch(`${API_BASE}/workspaces`, { headers });
                if (resp.ok) {
                    const backendProjects = await resp.json();
                    return backendProjects.map((p: any) => ({
                        id: p.id,
                        name: p.issue_description || 'Unnamed Project',
                        type: p.type || (p.parts_list?.length > 0 ? 'parts' : p.diagrams_list?.length > 0 ? 'diagrams' : 'chat'),
                        data: {
                            messages: p.chat_history || [],
                            results: p.parts_list || p.diagrams_list || []
                        },
                        vehicleContext: {
                            make: p.vehicle_make,
                            model: p.vehicle_model,
                            year: p.vehicle_year
                        },
                        createdAt: p.created_at,
                        updatedAt: p.updated_at
                    }));
                }
            } catch (err) {
                console.error("Failed to fetch projects from backend", err);
            }
        }
        return this.getProjectsLocal();
    },

    async getProject(id: string) {
        const headers = getHeaders();
        const userId = headers['X-User-Id'];

        if (userId) {
            try {
                const resp = await fetch(`${API_BASE}/workspaces/${id}`, { headers });
                if (resp.ok) {
                    const p = await resp.json();
                    return {
                        id: p.id,
                        name: p.issue_description || 'Unnamed Project',
                        type: p.parts_list?.length > 0 ? 'parts' : 'chat',
                        data: {
                            messages: p.chat_history,
                            results: p.parts_list
                        },
                        vehicleContext: {
                            make: p.vehicle_make,
                            model: p.vehicle_model,
                            year: p.vehicle_year
                        },
                        createdAt: p.created_at,
                        updatedAt: p.updated_at
                    };
                }
            } catch (err) {
                console.error("Failed to fetch project from backend", err);
            }
        }

        return this.getProjectsLocal().find((p: any) => p.id === id);
    },

    async deleteProject(id: string) {
        const headers = getHeaders();
        const userId = headers['X-User-Id'];

        if (userId) {
            try {
                await fetch(`${API_BASE}/workspaces/${id}`, {
                    method: 'DELETE',
                    headers
                });
            } catch (err) {
                console.error("Failed to delete project from backend", err);
            }
        }

        const projects = this.getProjectsLocal().filter((p: any) => p.id !== id);
        localStorage.setItem('diy_mech_projects', JSON.stringify(projects));
    },

    // --- ElectrAI Studio ---
    studio: {
        async architect(prompt: string, targetMCU: string = 'esp32') {
            const response = await fetch(`${API_BASE}/studio/architect`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ 
                    prompt, 
                    target_mcu: targetMCU,
                    vehicle_info: {
                        make: 'Electronics',
                        model: targetMCU,
                        year: 2026,
                        type: 'Electronics'
                    },
                    session_id: `studio-${Date.now()}`
                })
            });
            if (!response.ok) throw new Error(`Architect failed: ${response.status}`);
            return await response.json();
        },
        async generateWiring(code: string) {
            const response = await fetch(`${API_BASE}/studio/wiring`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ code })
            });
            if (!response.ok) throw new Error(`Wiring failed: ${response.status}`);
            return await response.json();
        },
        async generateBOM(code: string) {
            const response = await fetch(`${API_BASE}/studio/bom`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ code })
            });
            if (!response.ok) throw new Error(`BOM failed: ${response.status}`);
            return await response.json();
        },
        async analyzePower(code: string, board: string = 'esp32') {
            const response = await fetch(`${API_BASE}/studio/power`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ code, board })
            });
            if (!response.ok) throw new Error(`Power analysis failed: ${response.status}`);
            return await response.json();
        },
        async generateN8N(code: string) {
            const response = await fetch(`${API_BASE}/studio/bridge/n8n`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ code })
            });
            if (!response.ok) throw new Error(`n8n generation failed: ${response.status}`);
            return await response.json();
        },
        async generateDriver(datasheetExcerpt: string, className: string) {
            const response = await fetch(`${API_BASE}/studio/driver/generate`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ datasheet_excerpt: datasheetExcerpt, class_name: className })
            });
            if (!response.ok) throw new Error(`Driver generation failed: ${response.status}`);
            return await response.json();
        },
        async generateFab(code: string, blueprint: any) {
            const response = await fetch(`${API_BASE}/studio/fab`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ code, blueprint })
            });
            if (!response.ok) throw new Error(`Fab generation failed: ${response.status}`);
            return await response.json();
        },
        async analyzeSerial(logLines: string[], currentCode: string) {
            const response = await fetch(`${API_BASE}/studio/debug/serial`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ logs: logLines, code: currentCode })
            });
            if (!response.ok) throw new Error(`Serial analysis failed: ${response.status}`);
            return await response.json();
        },
        async compile(code: string, boardType: string = 'esp32:esp32:esp32') {
            const response = await fetch(`${API_BASE}/studio/compile`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ code, board_type: boardType })
            });
            if (!response.ok) throw new Error(`Compilation failed: ${response.status}`);
            return await response.json(); // Returns { hex: "...", bin: "..." }
        },
        async rescueAnalyze(code: string, logs: string, answers: any) {
            const response = await fetch(`${API_BASE}/studio/rescue/analyze`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ code, logs, answers })
            });
            if (!response.ok) throw new Error(`Rescue analysis failed: ${response.status}`);
            return await response.json();
            return await response.json();
        }
    }
};
